/**
 * 🔐 Role Permissions Management Page
 * Manage role permissions with an interactive matrix
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ExpandMore,
  Lock,
  LockOpen,
  Refresh,
  Save,
  Info,
} from '@mui/icons-material';
import { rolePermissionsService, RolePermissions } from '@/services/rolePermissionsService';
import { usePermissions } from '@/hooks/usePermissions';
import toast from 'react-hot-toast';

export const RolePermissionsPage: React.FC = () => {
  const [roles, setRoles] = useState<RolePermissions[]>([]);
  const [allPermissions, setAllPermissions] = useState<string[]>([]);
  const [permissionCategories, setPermissionCategories] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState<Map<string, string[]>>(new Map());
  const [saving, setSaving] = useState(false);

  const { isSuperAdmin } = usePermissions();

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const response = await rolePermissionsService.getRoles();

      if (response.success) {
        setRoles(response.roles || []);
        setAllPermissions(response.allPermissions || []);
        setPermissionCategories(
          response.permissionCategories || rolePermissionsService.organizePermissionsByCategory()
        );
        setPendingChanges(new Map());
        toast.success('Roles loaded successfully');
      } else {
        toast.error(response.error || 'Failed to load roles');
      }
    } catch (error) {
      console.error('Failed to load roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (roleId: string, permission: string, checked: boolean) => {
    // Find the role
    const role = roles.find(r => r.role === roleId);

    if (!role) return;

    // Check if editable
    if (!role.isEditable) {
      toast.error(`Cannot modify ${role.role} - it's a protected system role`);
      return;
    }

    // Get current permissions (from pending changes or original)
    const currentPermissions = pendingChanges.has(roleId)
      ? pendingChanges.get(roleId)!
      : role.permissions;

    // Update permissions
    const newPermissions = checked
      ? [...currentPermissions, permission]
      : currentPermissions.filter(p => p !== permission);

    // Store pending change
    const updatedPendingChanges = new Map(pendingChanges);
    updatedPendingChanges.set(roleId, newPermissions);
    setPendingChanges(updatedPendingChanges);
  };

  const hasPermission = (roleId: string, permission: string): boolean => {
    const role = roles.find(r => r.role === roleId);
    if (!role) return false;

    // Check pending changes first
    if (pendingChanges.has(roleId)) {
      const permissions = pendingChanges.get(roleId)!;
      return rolePermissionsService.hasPermission(permissions, permission);
    }

    return rolePermissionsService.hasPermission(role.permissions, permission);
  };

  const handleSaveChanges = async () => {
    if (pendingChanges.size === 0) {
      toast('No changes to save');
      return;
    }

    setSaving(true);
    try {
      // Update each role with changes
      const updates = Array.from(pendingChanges.entries()).map(([roleId, permissions]) =>
        rolePermissionsService.updateRolePermissions(roleId, permissions)
      );

      const results = await Promise.all(updates);

      const failedUpdates = results.filter(r => !r.success);

      if (failedUpdates.length === 0) {
        toast.success('All permissions updated successfully!');
        await loadRoles(); // Reload to get fresh data
      } else {
        toast.error(`Failed to update ${failedUpdates.length} role(s)`);
      }
    } catch (error) {
      console.error('Failed to save changes:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setPendingChanges(new Map());
    toast('Changes discarded');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Check if user can manage roles
  if (!isSuperAdmin()) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>Only super admins can manage role permissions.</Typography>
        </Alert>
      </Box>
    );
  }

  const hasPendingChanges = pendingChanges.size > 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            Role Permissions Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure permissions for each admin role
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadRoles}
            disabled={saving}
          >
            Refresh
          </Button>
          {hasPendingChanges && (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDiscardChanges}
                disabled={saving}
              >
                Discard Changes
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSaveChanges}
                disabled={saving}
              >
                Save {pendingChanges.size} Change{pendingChanges.size !== 1 ? 's' : ''}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }} icon={<Info />}>
        <Typography variant="body2">
          <strong>Tip:</strong> Toggle checkboxes to modify role permissions. Changes are saved to the
          database immediately when you click "Save Changes".
          <br />
          <Lock fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Protected roles (like super_admin) cannot be modified.
        </Typography>
      </Alert>

      {/* Roles Summary */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Available Roles
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {roles.map(role => (
            <Chip
              key={role.role}
              label={role.role}
              icon={role.isEditable ? <LockOpen /> : <Lock />}
              color={role.isSystemRole ? 'primary' : 'default'}
              variant={role.isEditable ? 'outlined' : 'filled'}
            />
          ))}
        </Box>
      </Card>

      {/* Permission Matrix by Category */}
      {Object.entries(permissionCategories).map(([category, permissions]) => (
        <Accordion key={category} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">{category}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {(permissions as string[]).length} permissions
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '30%' }}>Permission</TableCell>
                    {roles.map(role => (
                      <TableCell key={role.role} align="center" sx={{ fontWeight: 600 }}>
                        <Tooltip title={role.description || role.role}>
                          <Box>
                            {role.role}
                            {!role.isEditable && <Lock fontSize="small" sx={{ ml: 0.5 }} />}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(permissions as string[]).map(permission => (
                    <TableRow key={permission} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {permission}
                        </Typography>
                      </TableCell>
                      {roles.map(role => (
                        <TableCell key={role.role} align="center">
                          <Checkbox
                            checked={hasPermission(role.role, permission)}
                            disabled={!role.isEditable || saving}
                            onChange={(e) =>
                              handlePermissionToggle(role.role, permission, e.target.checked)
                            }
                            size="small"
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default RolePermissionsPage;
